// Libraries
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import get from 'lodash/get';
import cn from 'classnames';
import { Button, BUTTON_THEMES, Grid, Modal } from 'ht-styleguide';
import { queryClient } from '@integrations/reactquery';

/* Stores */
import { useResponsiveIsMobile, useResponsiveIsMobileOrTablet } from '@store/client/store.responsive';
import useAddSkuStore, { useDynamicPlanStore } from '@scenes/AddSkuPage/store/store.addsku';

/* Queries/Hooks/Mutations */
import useCartCache from '@store/queries/cart/useCart.cache';
import useCartQueryItemprice from '@store/queries/cart/useCartQuery.itemprice';
import useCartMutationAddToCart from '@store/queries/cart/useCartMutation.addtocart';
import useCartMutationUpdateitem from '@store/queries/cart/useCartMutation.updateitem';
import { skuQueryKeys } from '@store/queries/sku/queries.keys';
import useCartMutationStartBooking, { handleSingleServiceBookingSuccess } from '@store/queries/cart/useCartMutation.startbooking';
import { useIsPasswordlessCheckoutEnabled } from '@scenes/LoginPage/Modal/LoginPasscode/LoginPasscode.hooks';

/* Components */
import SubNav from '@components/SubNav';
import ButtonFooter from '@components/UI/ButtonFooter';
import PreQuestion from '@scenes/AddSkuPage/Parts/PreQuestion';
import AddSkuHeader from './Parts/AddSkuHeader';
import AddSkuPageDisclaimer from './Parts/AddSkuPageDisclaimer';
import AddSkuPageBanner from './Parts/AddSkuPageBanner';
import AddTopSkuNotice from './Parts/AddTopSkuNotice';
import AddSkuButton from './Parts/AddSkuButton';
import Maybe from '@components/Maybe';

/* Utils & Constants */
import { isValidNonEmptyArray } from '@utils/array';
import { addSkuPath, cartPath } from '@constants/paths';
import { isBrowser } from '@utils/ui';
import { OVERRIDE_BASE_PRICE_DISCLAIMER } from './addsku.constants';
import { checkPartnerCart, filterRemoteQuestions, getItemDisplayPrice, getErrors } from './addsku.utils';

/* Styles */
import styles from './addskupage.module.scss';

/* Types */
import { IHash } from 'types/base.types';
import { Question, Sku } from 'types/sku.types';
import { useCartFlow } from 'src/utils/cart/useCartFlow';
import { SubHint } from '@components/UI/Hints';
// import useBESplitMutationSkuPrice from '@hooks/useBESplitSkuPrice';

type AddSkuPageProps = {
  sku: Sku;
  questions: Question[];
  isConfirmation?: boolean;
  buttonStyles?: IHash<string> | string;
  showButtonPrice?: boolean;
  hideHeaderPrice?: boolean;
  withoutHeader?: boolean;
};

const AddSkuPage = (props: AddSkuPageProps) => {
  const {
    sku,
    questions: skuQuestions,
    buttonStyles = null,
    isConfirmation = false,
    showButtonPrice = false,
    hideHeaderPrice = false,
    withoutHeader = false,
  } = props;
  /* Local State */
  const [errors, setErrors] = useState({});
  const [cartMismatch, setCartMismatch] = useState(false);

  /* Hooks */
  const { actions, ...item } = useAddSkuStore();
  const { actions: dynamicPlanActions } = useDynamicPlanStore();
  const isMobileOrTablet = useResponsiveIsMobileOrTablet();
  const isMobile = useResponsiveIsMobile();
  const cart = useCartCache();
  const router = useRouter();
  const {
    query: { mode, index: queryIndex = 0, id: querySkuId = '' },
    push,
  } = router;
  const { isSingleServiceFlow } = useCartFlow();
  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();

  /* Queries */
  // const {data: cpsaSkuPrice} = useBESplitMutationSkuPrice(sku.id, {});
  const { data } = useCartQueryItemprice({ item, sku });
  const addToCartMutation = useCartMutationAddToCart();
  const updateItemMutation = useCartMutationUpdateitem();
  const startBookingMutation = useCartMutationStartBooking();

  /* Constants */
  const remoteSavingsFormatted = data?.breakdown?.adjustments?.[0]?.amount_formatted;
  const partnerMismatchCheck = checkPartnerCart(cart, sku);
  const questions = filterRemoteQuestions({ questions: skuQuestions, sku, item, cart });
  const { showDiscountAppliedLater } = sku;
  const itemDisplayPrice = getItemDisplayPrice({ item, total: data?.totalPrice ?? '' });
  const banner = sku?.skuMessagings?.banner;
  const disclaimer = sku?.skuMessagings?.disclaimer;
  const bullets = sku?.skuMessagings?.bullets;
  const hasBullets = Array.isArray(bullets) && bullets.length > 0;
  const isEdit = !!(mode === 'edit' && isValidNonEmptyArray(cart?.items));

  /* Grid Modifications */
  const gridStyles = hasBullets ? '' : styles.hideBulletsContainer;
  const gridWidth = hasBullets ? 5 : 6;
  const gridOffset = hasBullets ? 1 : 3;

  /* Styles */
  const addButtonStyles = cn(styles.buttonWrapper, buttonStyles);
  const secondaryHeaderWrapperStyle = cn('paddingX-small2', styles.subNavInnerClass, {
    [styles.withBullets]: hasBullets && isMobileOrTablet,
  });

  /*
       PARTNER-TODO: Remove this when partners is ready to implement this feature
       We don't handle fieldSales redirect to SPA site
   */
  useEffect(() => {
    if (cart.fieldSaleAgent && isBrowser()) {
      window.location.assign(`${process.env.NEXT_PUBLIC_PROXY_URL}${addSkuPath(querySkuId as string)}`);
    }
  }, [cart.fieldSaleAgent, querySkuId]);

  /*
   *  Update the dynamic plan savings in dropdown.
   *  - It only returns it when non-plan is selected so we get a mismatch of it existing, then it not.
   *    So, we need to store it state above the component root.
   * */
  useEffect(() => {
    if (remoteSavingsFormatted && sku?.id) {
      dynamicPlanActions.updateDynamicPlanSavings(remoteSavingsFormatted);
    }
  }, [remoteSavingsFormatted, sku?.id, dynamicPlanActions]);

  /*
   *  Seed initial state with sku values & questions
   * */
  useEffect(() => {
    actions.updateAddSkuAddItem({ sku, questions });
    document.body.style.setProperty('overflow', 'visible');

    return () => {
      dynamicPlanActions.resetDynamicPlanSavings();
      document.body.style.removeProperty('overflow');
    };
  }, [dynamicPlanActions, actions, questions, sku]);

  /*
   * When in edit mode, Seed initial state with sku values & questions from cart, prev saved
   * */
  useEffect(() => {
    if (isEdit) {
      const q = cart?.items[+queryIndex].questions;
      /* We get breakdown from the itemPrice call. No need to seed it locally */
      const breakdown = data?.breakdown;
      actions.updateAddSkuQuestions({ sku, questions: q, breakdown });
    }
  }, [isEdit, actions, dynamicPlanActions, questions, sku, cart?.items, data?.breakdown, queryIndex]);

  /* Lets set the server side sku data: read only */
  useEffect(() => {
    /* Set the Server Sku State: Not sure we need this? Since its SSG. Perhaps to verify what's in the page? hmm */
    queryClient.setQueryData(skuQueryKeys.addSku(sku?.id || 0), { ...sku, _questions: questions });
  }, [questions, sku]);

  /*
   * Allows us to decorate a function to guard with errors
   * */
  const verifyForm = (fn: GenericFunction<React.SyntheticEvent, any>) => (event: React.SyntheticEvent) => {
    const { errors: errs, hasErrors } = getErrors({ item, questions });

    if (hasErrors) {
      setErrors(errs);
    } else {
      fn(event);
    }
  };

  const addToCartMutationApi = () => {
    addToCartMutation.mutate(
      { item, email: null, sku },
      {
        onSuccess: () => {
          if (isSingleServiceFlow()) {
            startBookingMutation.mutate(undefined, {
              onSuccess: () => {
                handleSingleServiceBookingSuccess({ router, isPasswordless: isPasswordlessCheckoutEnabled });
              },
            });
          } else {
            push(cartPath());
          }
        },
      },
    );
  };

  const addToCart = verifyForm(async event => {
    event.preventDefault();

    const showItemRemovalDialog = checkPartnerCart(cart, sku).showDialog;
    if (showItemRemovalDialog) {
      setCartMismatch(true);
    } else {
      addToCartMutationApi();
    }
  });

  const updateItem = verifyForm(event => {
    event.preventDefault();

    updateItemMutation.mutate(
      {
        item,
        index: queryIndex as number,
        email: null,
      },
      {
        onSuccess: () => {
          push(cartPath());
        },
      },
    );
  });

  const renderSideBullets = () => {
    const bulletsPresent = bullets?.length > 0;

    return bulletsPresent && !isMobileOrTablet ? (
      <div className={styles.skuBullets}>
        <h4>IMPORTANT:</h4>
        <ul>
          {bullets.map((bullet, index) => (
            <li key={index}>
              <p className="p1 text-weight-book">{bullet}</p>
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  };

  /**
   * Defining Button Here cause of linting
   */
  const renderNextButton = () =>
    AddSkuButton({
      isEdit,
      itemDisplayPrice,
      showButtonPrice,
      isConfirmation,
      action: isEdit ? updateItem : addToCart,
      icon: 'cart',
    });

  return (
    <Grid.Fluid>
      <Grid.Row>
        <Maybe condition={!withoutHeader}>
          <SubNav innerClasses={secondaryHeaderWrapperStyle}>
            <AddSkuHeader
              sku={sku}
              hideHeaderPrice={hideHeaderPrice}
              isConfirmation={isConfirmation}
              itemDisplayPrice={itemDisplayPrice as string}
              showDiscountAppliedLater={showDiscountAppliedLater}
            />
            <AddTopSkuNotice bullets={bullets} />
          </SubNav>
        </Maybe>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column sm={4} md={8} lg={10} lgOffset={1}>
          <AddSkuPageBanner banner={banner} />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row classes={styles.contentWrapper}>
        <Grid.Column sm={4} md={8} lg={gridWidth} lgOffset={gridOffset}>
          <div className="form form-container -center">
            {questions.map((q: Question, index: number) => (
              <div key={index} className={styles.formElementWrapper}>
                <PreQuestion question={q} sku={sku} error={get(errors, q.id, '')} item={item!} />
                {q.subhint && <SubHint question={q} />}
              </div>
            ))}
            <div className={addButtonStyles}>
              {renderNextButton()}
              <Maybe condition={showDiscountAppliedLater}>
                <p className={styles.showDiscountAppliedLaterDisclaimer}>{OVERRIDE_BASE_PRICE_DISCLAIMER}</p>
              </Maybe>
            </div>
          </div>
          <Maybe condition={showDiscountAppliedLater}>
            <p className={styles.showDiscountAppliedLaterDisclaimer}>{OVERRIDE_BASE_PRICE_DISCLAIMER}</p>
          </Maybe>
          <Maybe condition={isMobile}>
            <ButtonFooter noZIndex className={styles.buttonFooterWrapper}>
              {renderNextButton()}
            </ButtonFooter>
          </Maybe>
          <AddSkuPageDisclaimer disclaimer={disclaimer} />
        </Grid.Column>
        <Grid.Column classes={gridStyles} lgOffset={1} lg={4}>
          {renderSideBullets()}
        </Grid.Column>
      </Grid.Row>
      {/* Do we have any mismatched cart items? */}
      <Maybe condition={cartMismatch}>
        <Modal
          isVisible={cartMismatch}
          hide={() => setCartMismatch(false)}
          onRequestClose={() => setCartMismatch(false)}
          header={partnerMismatchCheck.title}
          footerElement2={
            <Button theme={BUTTON_THEMES.SECONDARY} onClick={() => setCartMismatch(false)}>
              No
            </Button>
          }
          footerElement3={
            <Button
              onClick={() => {
                setCartMismatch(false);
                addToCartMutationApi();
              }}
            >
              Yes
            </Button>
          }
        >
          {partnerMismatchCheck.description}
        </Modal>
      </Maybe>
    </Grid.Fluid>
  );
};

export default AddSkuPage;
